import { useEffect, useState } from 'preact/hooks'
import { AlertTriangle, Clock } from 'lucide-preact'
import { isTerminalBlastState, type EmailBlastViewDto } from '@/types/emailBlast'
import { relativeFromNow } from '../engagementFormat'

// The engine ticks every five minutes, so a correct page legitimately sits still for minutes at a time.
// Past two ticks that stops being normal and starts being a signal: the realtime connection can drop
// without any visible sign, and this stamp is the only thing that would tell an operator.
const STALE_AFTER_MS = 12 * 60 * 1000
const TICK_MS = 30 * 1000

const CADENCE_HINT =
  'The send checker runs every 5 minutes, so these numbers can sit still for a few minutes even while ' +
  'emails are going out.'

/**
 * How fresh the numbers are (ORB-706). Deliberately relative, never an absolute instant: a precise
 * timestamp implies precise measurement of the SEND, when what it actually describes is when we last
 * counted.
 */
export function BlastFreshness({ blast, onRefresh }: { blast: EmailBlastViewDto; onRefresh: () => void }) {
  // Re-render on a timer so "2 min ago" ages on screen rather than only on refetch.
  const [, setNow] = useState(0)
  useEffect(() => {
    if (isTerminalBlastState(blast.State)) return
    const id = setInterval(() => setNow(value => value + 1), TICK_MS)
    return () => clearInterval(id)
  }, [blast.State])

  // Send totals freeze when a blast finishes; opens and clicks do not, which is why Engagement carries
  // its own line rather than sharing this one. "Updated just now" on a blast that ended last Tuesday
  // reads as though something is still happening.
  if (isTerminalBlastState(blast.State)) {
    const endedAt = blast.CompletedAtUtc ? new Date(blast.CompletedAtUtc) : null
    const verb = blast.State === 'Complete' ? 'Finished' : blast.State === 'Cancelled' ? 'Cancelled' : 'Stopped'
    return (
      <p className="text-xs text-slate-400 dark:text-slate-500">
        {endedAt && !Number.isNaN(endedAt.getTime())
          ? `${verb} ${endedAt.toLocaleString()} · send totals are final`
          : 'Send totals are final'}
      </p>
    )
  }

  const countedAt = blast.StatsAsOfUtc ? new Date(blast.StatsAsOfUtc) : null
  if (!countedAt || Number.isNaN(countedAt.getTime())) return null

  const isStale = Date.now() - countedAt.getTime() > STALE_AFTER_MS

  if (isStale) {
    return (
      <p className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        Updated {relativeFromNow(blast.StatsAsOfUtc)} — this may be out of date.{' '}
        <button
          onClick={onRefresh}
          className="underline underline-offset-2 hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        >
          Refresh
        </button>
      </p>
    )
  }

  return (
    <p className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500" title={CADENCE_HINT}>
      <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      Updated {relativeFromNow(blast.StatsAsOfUtc)}
    </p>
  )
}
